import { Link } from 'react-router-dom';
import { Map, Sparkles, Calendar, Users, ArrowRight, Plane } from 'lucide-react';
import type { User } from '../types/database';

interface LandingPageProps {
  user: User | null;
}

export default function LandingPage({ user }: LandingPageProps) {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <Plane className="h-8 w-8 text-primary-600" />
              <span className="text-xl font-bold text-gray-900">VoyageAI</span>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-primary-600 transition">Features</a>
              <a href="#how-it-works" className="text-gray-600 hover:text-primary-600 transition">How it Works</a>
              {user ? (
                <Link to="/dashboard" className="btn-primary">
                  Dashboard
                </Link>
              ) : (
                <Link to="/auth" className="btn-primary">
                  Get Started
                </Link>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-100/50 to-accent-100/50" />
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="h-4 w-4" />
              <span>AI-Powered Travel Planning</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Plan Your Dream Trip with
              <span className="text-primary-600"> AI</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Chat with our AI travel assistant to create personalized, bookable day-by-day itineraries. 
              Find the best flights, hotels, and activities tailored just for you.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/auth" className="btn-primary text-lg px-8 py-4 flex items-center justify-center">
                Start Planning Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a href="#how-it-works" className="btn-secondary text-lg px-8 py-4">
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Everything You Need for Perfect Trips</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard 
              icon={<Sparkles className="h-8 w-8 text-primary-600" />}
              title="AI-Powered Planning"
              description="Our AI creates personalized itineraries based on your preferences and budget."
            />
            <FeatureCard 
              icon={<Map className="h-8 w-8 text-primary-600" />}
              title="Interactive Maps"
              description="Visualize your trip on beautiful maps with all your stops and attractions."
            />
            <FeatureCard 
              icon={<Calendar className="h-8 w-8 text-primary-600" />}
              title="Smart Scheduling"
              description="Optimized day-by-day plans with timing, transportation, and booking info."
            />
            <FeatureCard 
              icon={<Users className="h-8 w-8 text-primary-600" />}
              title="Collaborate"
              description="Plan trips with friends and family, vote on activities together."
            />
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <StepCard 
              number="1"
              title="Tell Us About Your Trip"
              description="Share your destination, dates, budget, and travel style with our AI assistant."
            />
            <StepCard 
              number="2"
              title="Get AI Recommendations"
              description="Receive a personalized itinerary with flights, hotels, and activities tailored to you."
            />
            <StepCard 
              number="3"
              title="Book & Go"
              description="Export your itinerary to PDF or calendar, then enjoy your perfect trip!"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Plan Your Next Adventure?</h2>
          <p className="text-primary-100 text-lg mb-8">
            Join thousands of travelers who've discovered the easiest way to plan perfect trips.
          </p>
          <Link to="/auth" className="inline-flex items-center bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary-50 transition">
            Get Started Free
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Plane className="h-6 w-6" />
              <span className="text-lg font-bold text-white">VoyageAI</span>
            </div>
            <p>&copy; 2024 VoyageAI. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}

function StepCard({ number, title, description }: { number: string; title: string; description: string }) {
  return (
    <div className="relative bg-white rounded-2xl p-8 shadow-lg">
      <div className="absolute -top-4 left-8 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
        {number}
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}
